import { isString } from 'lodash';
import { makeApi, makeBenefitsApi } from 'src/http/api/internal/internal-http';
import { PromotionsRepository } from 'src/repositories/benefits/promotions.repository';
import { PromotionManager, promotionsListTypeItem } from 'src/types/promotions';
import { toBase64 } from 'src/utils/utils';
import { create } from 'zustand';

type States = {
  isLoadingPromotionsList: boolean;
  isLoadingPromotionDetails: boolean;
  isLoadingCreatePromotion: boolean;
  isLoadingUpdatePromotion: boolean;
  promotionsList: promotionsListTypeItem[];
  promotionDetails: PromotionManager | null;
};

type Actions = {
  getPromotionsList: () => Promise<void>;
  getPromotionDetails: (id: string) => Promise<void>;
  resetPromotionDetails: () => void;
  createPromotion: (promotion: PromotionManager) => Promise<void>;
  updatePromotion: (id: string, promotion: PromotionManager) => Promise<void>;
};

type Promotions = Actions & States;

const initialValues = {
  isLoadingPromotionsList: false,
  isLoadingPromotionDetails: false,
  isLoadingCreatePromotion: false,
  isLoadingUpdatePromotion: false,
  promotionsList: [],
  promotionDetails: null,
};

const promotionsRepository = new PromotionsRepository(makeBenefitsApi());
const authRepository = new PromotionsRepository(makeApi());

export const usePromotionsStore = create<Promotions>((set: any, get: any) => ({
  ...initialValues,

  getPromotionsList: async () => {
    set({ isLoadingPromotionsList: true });
    const response = await promotionsRepository.getPromotionsList();

    if (response.isLeft()) {
      set({ isLoadingPromotionsList: false });
      throw new Error(response.value.message);
    } else {
      set({ promotionsList: response.value, isLoadingPromotionsList: false });
    }
  },

  getPromotionDetails: async (id) => {
    set({ isLoadingPromotionDetails: true });
    const response = await promotionsRepository.getPromotionDetails(id);

    if (response.isLeft()) {
      set({ isLoadingPromotionDetails: false });
      throw new Error(response.value.message);
    } else {
      set({ promotionDetails: response.value, isLoadingPromotionDetails: false });
    }
  },

  resetPromotionDetails: () => {
    set({ promotionDetails: null });
  },

  createPromotion: async (promotion) => {
    set({ isLoadingCreatePromotion: true });

    if (!isString(promotion.image)) {
      try {
        const imgBase64: any = await toBase64(promotion.image);
        const res: any = await authRepository.uploadFile({
          name: promotion.image.name,
          base64: imgBase64,
        });
        promotion.image = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (Array.isArray(promotion.banners)) {
      try {
        const bannersProcessed = await Promise.all(
          promotion.banners.map(async (banner) => {
            if (typeof banner !== 'string' && 'name' in banner) {
              const bannerBase64: any = await toBase64(banner);
              const res: any = await authRepository.uploadFile({
                name: banner.name,
                base64: bannerBase64,
              });
              return res.value.data.url;
            }
            throw new Error('Invalid banner format');
          })
        );
        promotion.banners = bannersProcessed;
      } catch (error) {
        console.error(error);
      }
    }

    const response = await promotionsRepository.createPromotion(promotion);

    if (response.isLeft()) {
      set({ isLoadingCreatePromotion: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingCreatePromotion: false });
    }
  },

  updatePromotion: async (id, promotion) => {
    set({ isLoadingUpdatePromotion: true });

    if (!isString(promotion.image)) {
      try {
        const imgBase64: any = await toBase64(promotion.image);
        const res: any = await promotionsRepository.uploadFile({
          name: promotion.image.name,
          base64: imgBase64,
        });
        promotion.image = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }

    if (Array.isArray(promotion.banners)) {
      try {
        const bannersProcessed = await Promise.all(
          promotion.banners.map(async (banner) => {
            if (typeof banner !== 'string' && 'name' in banner) {
              const bannerBase64: any = await toBase64(banner);
              const res: any = await promotionsRepository.uploadFile({
                name: banner.name,
                base64: bannerBase64,
              });
              return res.value.data.url;
            }
            throw new Error('Invalid banner format');
          })
        );
        promotion.banners = bannersProcessed;
      } catch (error) {
        console.error(error);
      }
    }

    const response = await promotionsRepository.updatePromotion(id, promotion);

    if (response.isLeft()) {
      set({ isLoadingUpdatePromotion: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingUpdatePromotion: false });
    }
  },
}));
